import {
  Button,
  Table,
  TableCell,
  TableColumnHeader,
  TableRow,
  TableRowHeader
} from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { InstallationFormData } from '../../pages/supplier-details/components/InstallationForm.schema'

type Props = {
  data: InstallationFormData
  onNextClick: () => void
  onBackClick: () => void
  isPending: boolean
}

export default function InstallationReviewStep({
  onNextClick,
  onBackClick,
  data,
  isPending: isPendingCreatePartnershipWithInstallation
}: Props) {
  const { t } = useI18n()

  const handleNext = () => {
    return onNextClick()
  }

  return (
    <div className="space-y-6 pb-2">
      <div>
        <h2 className="text-xl font-semibold mb-2">
          {t('installations:createInstallationDialog.steps.descriptions.review.title')}
        </h2>
        <p className="text-sm text-text-secondary mb-6">
          {t('installations:createInstallationDialog.steps.descriptions.review.description')}
        </p>
        <div className="grid grid-cols-2 gap-4 items-start">
          <Table superDense className="table-fixed">
            <thead>
              <TableRow>
                <TableColumnHeader colSpan={2}>
                  {t('installations:installationInputFields.sections.installationDetails')}
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody className="text-sm">
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('installations:installationInputFields.name.label')}
                </TableRowHeader>
                <TableCell>{data?.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('installations:installationInputFields.economicActivity.label')}
                </TableRowHeader>
                <TableCell>{t(`common:goodsFamily.${data?.goodsFamily}`)}</TableCell>
              </TableRow>
            </tbody>
          </Table>

          <Table superDense className="table-fixed">
            <thead>
              <TableRow>
                <TableColumnHeader colSpan={2}>
                  {t('installations:installationInputFields.sections.address')}
                </TableColumnHeader>
              </TableRow>
            </thead>
            <tbody className="text-sm">
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.street.label')}
                </TableRowHeader>
                <TableCell>{data?.street}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.streetNumber.label')}
                </TableRowHeader>
                <TableCell>{data?.streetNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left text-nowrap truncate">
                  {t('common:inputFields.street2.label')}
                </TableRowHeader>
                <TableCell>{data?.street2 || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.postCode.label')}
                </TableRowHeader>
                <TableCell>{data?.postCode}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.city.label')}
                </TableRowHeader>
                <TableCell>{data?.city}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('common:inputFields.poBox.label')}
                </TableRowHeader>
                <TableCell>{data?.poBox || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('installations:installationInputFields.unlocode.label')}
                </TableRowHeader>
                <TableCell>{data?.unlocode}</TableCell>
              </TableRow>
              <TableRow>
                <TableRowHeader className="text-left">
                  {t('installations:installationInputFields.longitude.label')} /{' '}
                  {t('installations:installationInputFields.latitude.label')}
                </TableRowHeader>
                <TableCell>
                  {data?.longitude} / {data?.latitude}
                </TableCell>
              </TableRow>
            </tbody>
          </Table>
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.back')}
        </Button>
        <Button
          isLoading={isPendingCreatePartnershipWithInstallation}
          disabled={isPendingCreatePartnershipWithInstallation}
          onClick={handleNext}
        >
          {t('suppliers:createSupplierDialog.actions.add')}
        </Button>
      </div>
    </div>
  )
}
